import { fetchFastApi } from "../../api/fastapi";
import { useHookCacheQuery } from "../../lib/hookCache";
import {
  ProcedureSessionTableShell,
  procedureSessionTableShellSchema,
} from "../../models/audit/procedureSessionTableShell";

/**
 * Hook for the new Phase 3 table-shell endpoint:
 * `GET /controls/v2/procedure-session/{ps_id}/table-shell`.
 *
 * Returns the light per-procedure shape used by the work-paper view:
 * `{ procedureSession, procedure, documents, sourceRows }` — source rows are
 * dry (no `check_results` / `attribute_test_results` / `match_items`). Heavy
 * per-row hydration uses `useSourceRow(psId, srId)` on selection.
 *
 * Cache key `['procedure-session', psId, 'table-shell']` deliberately
 * shares the `['procedure-session', psId]` prefix used by
 * `useControlSessionStatus`'s `invalidate` calls, so workPaperState
 * transitions blow away the table-shell entry the same way they already
 * cascade across documents + source-rows in Phase 2.
 */
export function useProcedureSessionTableShell(psId: string | undefined) {
  return useHookCacheQuery<ProcedureSessionTableShell>({
    key: ["procedure-session", psId ?? "", "table-shell"],
    fetcher: async () => {
      const response = await fetchFastApi({
        method: "GET",
        path: `/controls/v2/procedure-session/${psId}/table-shell`,
      });
      return procedureSessionTableShellSchema.parse(response);
    },
    enabled: !!psId,
  });
}
